import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

export type QuillEditorPreset = "article" | "note";

export type QuillEditorHandle = {
  getHTML: () => string;
  setHTML: (html: string) => void;
  clear: () => void;
  focus: () => void;
};

type QuillEditorProps = {
  preset: QuillEditorPreset;
  initialHTML?: string;
  placeholder?: string;
  className?: string;
  onChange?: (html: string) => void;
  onReady?: () => void;
  onError?: () => void;
};

type QuillInstance = {
  root: HTMLElement;
  on: (event: string, handler: () => void) => void;
  setText: (text: string) => void;
  focus: () => void;
  clipboard: { dangerouslyPasteHTML: (html: string) => void };
  getModule: (name: string) => unknown;
  enable: (enabled: boolean) => void;
};

const ARTICLE_TOOLBAR = [
  [{ header: [1, 2, 3, 4, false] }],
  ["bold", "italic", "underline", "strike"],
  [{ color: [] }, { background: [] }],
  [{ list: "ordered" }, { list: "bullet" }, { list: "check" }],
  ["blockquote", "code-block"],
  [{ align: [] }],
  ["link", "image", "table"],
  ["clean"],
];

const NOTE_TOOLBAR = [
  ["bold", "italic", "underline"],
  [{ list: "ordered" }, { list: "bullet" }],
  ["blockquote"],
  ["link"],
  ["clean"],
];

const TABLE_ICON = '<svg viewBox="0 0 18 18"><rect class="ql-stroke" height="12" width="12" x="3" y="3"></rect><line class="ql-stroke" x1="3" x2="15" y1="7" y2="7"></line><line class="ql-stroke" x1="3" x2="15" y1="11" y2="11"></line><line class="ql-stroke" x1="7" x2="7" y1="3" y2="15"></line><line class="ql-stroke" x1="11" x2="11" y1="3" y2="15"></line></svg>';

let tableIconRegistered = false;

/**
 * Reusable Quill rich-text editor. The `article` preset enables the full
 * toolbar (headings, colors, lists/checklists, blockquote/code, links, images,
 * tables), while the `note` preset exposes only email-safe essentials. Content
 * is surfaced to the parent as raw HTML via `onChange`; sanitization happens
 * when the parent persists the value.
 */
export const QuillEditor = forwardRef<QuillEditorHandle, QuillEditorProps>(function QuillEditor(
  { preset, initialHTML, placeholder, className, onChange, onReady, onError },
  ref,
) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const quillRef = useRef<QuillInstance | null>(null);
  const onChangeRef = useRef(onChange);
  const isInternalUpdateRef = useRef(false);

  onChangeRef.current = onChange;

  useImperativeHandle(ref, () => ({
    getHTML: () => quillRef.current?.root.innerHTML ?? "",
    setHTML: (html: string) => {
      const quill = quillRef.current;

      if (!quill) {
        return;
      }

      isInternalUpdateRef.current = true;

      if (html.trim()) {
        quill.clipboard.dangerouslyPasteHTML(html);
      } else {
        quill.setText("");
      }

      isInternalUpdateRef.current = false;
    },
    clear: () => {
      isInternalUpdateRef.current = true;
      quillRef.current?.setText("");
      isInternalUpdateRef.current = false;
    },
    focus: () => quillRef.current?.focus(),
  }), []);

  useEffect(() => {
    let disposed = false;

    async function setupEditor() {
      if (!containerRef.current || quillRef.current) {
        return;
      }

      try {
        const quillModule = await import("quill");

        if (disposed || !containerRef.current) {
          return;
        }

        const Quill = quillModule.default;

        if (!tableIconRegistered) {
          const icons = Quill.import("ui/icons") as Record<string, string>;
          icons.table = TABLE_ICON;
          tableIconRegistered = true;
        }

        const editorElement = document.createElement("div");
        containerRef.current.appendChild(editorElement);

        const quill = new Quill(editorElement, {
          theme: "snow",
          placeholder,
          modules: {
            table: preset === "article",
            toolbar: {
              container: preset === "article" ? ARTICLE_TOOLBAR : NOTE_TOOLBAR,
              handlers: preset === "article"
                ? {
                    table() {
                      const tableModule = quill.getModule("table") as { insertTable: (rows: number, columns: number) => void } | undefined;
                      tableModule?.insertTable(3, 3);
                    },
                  }
                : {},
            },
          },
        }) as unknown as QuillInstance;

        quillRef.current = quill;

        // Quill only drops the `.ql-blank` class (which renders the placeholder
        // via CSS) on `text-change`, but that event does not fire while an IME
        // composition is in progress. Without this, composing text (e.g. Japanese)
        // is painted directly on top of the placeholder until the IME commits.
        // Toggling a marker class lets CSS hide the placeholder during composition.
        const handleCompositionStart = () => quill.root.classList.add("ql-composing");
        const handleCompositionEnd = () => quill.root.classList.remove("ql-composing");
        quill.root.addEventListener("compositionstart", handleCompositionStart);
        quill.root.addEventListener("compositionend", handleCompositionEnd);

        if (initialHTML && initialHTML.trim()) {
          isInternalUpdateRef.current = true;
          quill.clipboard.dangerouslyPasteHTML(initialHTML);
          isInternalUpdateRef.current = false;
        }

        quill.on("text-change", () => {
          if (isInternalUpdateRef.current) {
            return;
          }

          onChangeRef.current?.(quill.root.innerHTML);
        });

        if (!disposed) {
          onReady?.();
        }
      } catch {
        if (!disposed) {
          onError?.();
        }
      }
    }

    void setupEditor();

    return () => {
      disposed = true;
      quillRef.current = null;

      if (containerRef.current) {
        containerRef.current.innerHTML = "";
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <div ref={containerRef} className={className} />;
});
